import { useFiscalStore } from "@/store/fiscal-store";
import { Progress } from "@/components/ui/progress";
import { Check, Loader2, Circle } from "lucide-react";
import { fmtBytes, fmtNum } from "@/lib/format";
import type { PipelinePhase } from "@/lib/fiscal-types";

const PHASES: { key: PipelinePhase; label: string; unit: "bytes" | "count" }[] = [
  { key: "upload", label: "1. Upload", unit: "bytes" },
  { key: "extract", label: "2. Descompactação", unit: "bytes" },
  { key: "process", label: "3. Processamento em lotes", unit: "count" },
  { key: "consolidate", label: "4. Consolidação", unit: "count" },
];

export default function PipelineStatus() {
  const { state, message, phase, progress } = useFiscalStore();
  if (state === "idle") return null;

  return (
    <div className="glass-card p-4 space-y-3 animate-in-card hover-glow flex-1 flex flex-col justify-center" style={{ animationDelay: '150ms' }}>
      <div className="flex items-center justify-between border-b border-white/10 pb-2">
        <div>
          <div className="text-[8px] uppercase tracking-[0.2em] text-primary font-black mb-0.5">Pipeline Status</div>
          <div className="font-bold text-slate-900 dark:text-white flex items-center gap-2 text-xs">
            {state === 'processing' && <Loader2 className="size-3 animate-spin text-primary" />}
            {message || state}
          </div>
        </div>
        <div className={`text-[8px] font-black px-1.5 py-0.5 rounded-full uppercase tracking-widest border ${
          state === 'finished' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 
          state === 'error' ? 'bg-red-500/10 text-red-500 border-red-500/20' : 
          'bg-blue-500/10 text-blue-500 border-blue-500/20'
        }`}>
          {state}
        </div>
      </div>

      <div className="space-y-3">
        {PHASES.map((p) => {
          const pr = progress[p.key];
          const pct = pr.total > 0 ? Math.min(100, (pr.current / pr.total) * 100) : 0;
          const done = pr.total > 0 && pr.current >= pr.total &&
            (PHASES.findIndex((x) => x.key === phase) > PHASES.findIndex((x) => x.key === p.key) ||
              state === "finished");
          const active = phase === p.key && state !== "finished" && state !== "error";
          const fmt = (n: number) => p.unit === "bytes" ? fmtBytes(n) : fmtNum(n);

          return (
            <div key={p.key} className="transition-all duration-300">
              <div className="flex items-center justify-between text-[10px] mb-1">
                <div className="flex items-center gap-2">
                  <div className={`size-5 rounded-md flex items-center justify-center border transition-all duration-300 ${
                    done ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-500' : 
                    active ? 'bg-primary/20 border-primary/30 text-primary animate-pulse' : 
                    'bg-slate-100 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-400'
                  }`}>
                    {done ? <Check className="size-3 stroke-[3]" /> : <Circle className="size-2 fill-current opacity-40" />}
                  </div>
                  <span className={`font-bold tracking-tight ${done ? "text-slate-900 dark:text-slate-200" : active ? "text-primary dark:text-glow" : "text-slate-500 dark:text-slate-500"}`}>
                    {p.label}
                  </span>
                </div>
                <div className="text-[9px] font-mono font-bold text-slate-500 dark:text-slate-400 tabular-nums">
                  {pr.total > 0 ? `${fmt(pr.current)} / ${fmt(pr.total)}` : "Wait"}
                </div>
              </div>
              <div className="relative h-1 w-full bg-slate-100 dark:bg-white/5 rounded-full overflow-hidden">
                <div 
                  className={`absolute left-0 top-0 h-full transition-all duration-1000 ease-out rounded-full ${
                    done ? 'bg-emerald-500' : 
                    active ? 'bg-primary shadow-glow' : 
                    'bg-slate-300 dark:bg-slate-700'
                  }`}
                  style={{ width: `${done ? 100 : pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
